import { nodesFlux, useEditorSelector } from '@candulabs/craft';
import { BrandPurple, color, css, Darker, GlitterGray, Lighter, styled } from 'canoe';
import React, { ReactElement, useCallback, useMemo, useRef } from 'react';

import Node from 'craft/components/Node';
import { ColumnButtons } from 'craft/components/SelectedToolbar/Columns/ColumnButtons';
import { BuiltInComponents } from 'craft/constants';
import { useNodeContext, useNodesData } from 'craft/hooks';
import { useColumnOperations } from 'craft/hooks/useColumnOperations';
import { useViewportBreakpoint } from 'craft/state';
import {
  CraftNode,
  CustomNodeData,
  MediaBreakpoint,
  Resolver,
  ToolboxComponentSections,
} from 'craft/types';
import { hasDescendants } from 'craft/utils/random';
import { getDomNodeFromDocument } from 'craft/utils/viewport';
import { subtleColor } from 'styleguide/typography/constants';

import { ColumnWidthAgent } from './ColumnWidthAgent';
import { ColumnSettings } from './toolbox/ColumnSettings';
import { Direction } from './types';
import { useHasFocusWithinColumnSet } from './useHasFocusWithinColumnSet';

const middleLine = (lineColor: string) => {
  return `linear-gradient(${lineColor}, ${lineColor}) no-repeat center/2px 100%`;
};

// Drag cursors are slightly larger than the column gap, this makes the edges of elements draggable and also handles
// 0 width gaps quite elegantly
const DragCursor = styled('div')<{ size: number }>`
  ${({ theme, size }) => css`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    margin-right: -${size + 4}px;
    width: ${size + 4}px;
    z-index: 2;
    cursor: ew-resize;

    &.column-drag-active {
      background: ${middleLine(theme.colors.BrandPurple)};
      z-index: 3;
      mix-blend-mode: difference;
    }
  `}
`;

const Placeholder = styled.div`
  display: flex;
  flex-grow: 1;
  min-height: 100%;
  min-width: 0;
  border-radius: 2px;
  align-items: center;
  justify-content: center;
  padding: 1em;

  font-weight: 400;
`;

const PlaceholderText = styled.p`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-bottom: 0;

  color: ${subtleColor};
`;

const ColumnNode = styled(({ hasFocus, hasChildren, ...props }) => <Node {...props} />)`
  ${({ hasFocus, hasChildren }) => {
    if (hasChildren && !hasFocus) {
      return '';
    }
    return `
      outline: 1px dashed ${hasFocus ? color(BrandPurple, Lighter) : color(GlitterGray, Darker)};
    `;
  }}
`;

export interface ColumnCustom extends CustomNodeData {
  custom: {
    width: string;
  };
}

export interface ColumnProps {
  id: string;
  children?: ReactElement | ReactElement[];
  custom: ColumnCustom;
}

export const COLUMN_DEFAULT_STYLES = {
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
};

export const Column: Resolver<ColumnProps> = ({ children }: ColumnProps) => {
  const { id } = useNodeContext();
  const viewportBreakpoint = useViewportBreakpoint();
  const customSelector = useMemo(() => nodesFlux.selectors.getNodeCustom(id), [id]);
  const hasChildren = hasDescendants(children);
  const [nodeData] = useNodesData([id]);

  const custom = useEditorSelector(customSelector);
  const { hasFocus } = useHasFocusWithinColumnSet(nodeData.parent);
  const agentRef = useRef<ColumnWidthAgent>();

  const { growColumn, shrinkColumn } = useColumnOperations({ parentNodeId: nodeData.parent });
  const [parentData] = useNodesData([nodeData.parent]);

  const setupAgent = (cursor: HTMLElement) => {
    const parentElement = getDomNodeFromDocument(nodeData.parent);
    const columnElement = getDomNodeFromDocument(nodeData.id);
    if (!parentElement || !columnElement) {
      return;
    }

    // Set up offset for cursor, as they're position: absolute within Columns they all stack at the end
    const parentDimensions = parentElement.getBoundingClientRect();
    const dimensions = columnElement.getBoundingClientRect();
    const cursorLeft = dimensions.left - parentDimensions.left + dimensions.width;

    // eslint-disable-next-line no-param-reassign
    cursor.style.left = `${cursorLeft}px`;

    const handleDragEnd = (direction: Direction, size: number) => {
      if (!growColumn || !shrinkColumn) {
        return;
      }

      // this is currently weird, we have separate concepts for growing and shrinking columns
      // this made sense when we had 4 drag operations (push / pull x left / right)
      // now we only have one handle on the right, we both grow and shrink on the right side
      // we can simplify this :)
      if (direction === Direction.Left) {
        shrinkColumn(id, Direction.Right, size);
      } else {
        growColumn(id, Direction.Right, size);
      }
    };

    if (agentRef.current) {
      agentRef.current?.tearDown();
      agentRef.current = undefined;
    }

    agentRef.current = new ColumnWidthAgent(parentElement, cursor, handleDragEnd);
  };

  const siblingData = useNodesData(parentData.nodes);
  const siblingsKey = siblingData.reduce(
    (acc, child) => `${acc + child.id}:${child.custom.custom?.width}:`,
    '',
  );

  const onCursorRef = useCallback(
    (cursor: HTMLElement | null) => {
      if (!cursor) {
        if (agentRef.current) {
          agentRef.current.tearDown();
          agentRef.current = undefined;
        }
        return;
      }
      setupAgent(cursor);
    },
    [growColumn, shrinkColumn],
  );

  if (!custom.custom) {
    // No custom most likely means node is in the process of being deleted
    return <></>;
  }
  const { width } = custom?.custom;
  const columnGap = parseInt(parentData.custom.css.columnGap || '0px', 10);
  const offset = parentData.nodes.findIndex((childNodeId) => childNodeId === id);
  const isLastElement = offset === parentData.nodes.length - 1;

  return (
    <>
      <ColumnNode
        className="column"
        data-cy="node-column"
        hasChildren={hasChildren}
        hasFocus={hasFocus}
        style={{ gridColumnStart: `span ${width}` }}
      >
        {hasChildren && children}
        {!hasChildren && (
          <Placeholder>
            <PlaceholderText>Drag any element into this area.</PlaceholderText>
          </Placeholder>
        )}
      </ColumnNode>
      {hasFocus && !isLastElement && viewportBreakpoint === MediaBreakpoint.LG ? (
        <DragCursor key={siblingsKey} size={columnGap} ref={onCursorRef} />
      ) : null}
    </>
  );
};

Column.craft = {
  name: BuiltInComponents.Column,
  isCanvas: true,
  rules: {
    canMoveIn: (incomingNodes) => {
      // Columns > Column > Column is invalid
      return incomingNodes.every((node) => node.data.name !== BuiltInComponents.Column);
    },
    canDrop: (target: CraftNode) => target.data.name === BuiltInComponents.Columns,
  },
};

Column.resize = {
  shouldDisableHandlers: true,
};

Column.toolbox = {
  schema: {
    [ToolboxComponentSections.Layout]: {
      isVisible: false,
    },
    [ToolboxComponentSections.Size]: {
      isVisible: false,
    },
  },
  component: [ColumnSettings],
};

Column.selectedToolbar = {
  customItems: [ColumnButtons],
};
